import { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { getChapters, upsertChapter } from '@/services/adminService';
import ChapterManager from '@/components/ChapterManager';
import type { Chapter } from '@dnyaneshwari/shared';
import { CHAPTERS } from '@dnyaneshwari/shared';

function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Import Modal ──────────────────────────────────────────────────────────

// ─── Main ContentPage ──────────────────────────────────────────────────────

interface ChapterRow {
  chapter: (typeof CHAPTERS)[number];
  firestoreData: Chapter | null;
}

export default function ContentPage() {
  const [rows, setRows] = useState<ChapterRow[]>(() =>
    CHAPTERS.map((ch) => ({ chapter: ch, firestoreData: null }))
  );
  const [expanded, setExpanded] = useState<string | null>(null);

  async function loadCounts() {
    const firestoreChapters = await getChapters();
    const chapterMap = new Map(firestoreChapters.map((c) => [c.id, c]));

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        firestoreData: chapterMap.get(String(row.chapter.number).padStart(2, '0')) ?? null,
      }))
    );
  }

  useEffect(() => { loadCounts(); }, []);

  async function seedChapters() {
    for (const ch of CHAPTERS) {
      const id = String(ch.number).padStart(2, '0');
      await upsertChapter({
        id,
        number: ch.number,
        titleMarathi: ch.titleMarathi,
        titleEnglish: ch.titleEnglish,
        titleSanskrit: ch.titleSanskrit,
        descriptionMarathi: '',
        descriptionEnglish: '',
        totalOvis: 0,
        totalShlokas: 0,
        imageUrl: '',
        audioSegments: [],
        sortOrder: ch.number,
      });
    }
    await loadCounts();
  }

  const chaptersInFirestore = rows.filter((r) => r.firestoreData !== null).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content</h1>
          <p className="text-sm text-gray-500 mt-1">Manage chapters, ovis, and audio</p>
        </div>
        <div className="flex gap-2">
          {chaptersInFirestore < 18 && (
            <button
              onClick={seedChapters}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Seed 18 Chapters
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            Chapters — {chaptersInFirestore}/18 seeded
          </h3>
        </div>

        <div className="divide-y divide-gray-100">
          {rows.map((row) => {
            const chId = String(row.chapter.number).padStart(2, '0');
            const isExpanded = expanded === chId;
            const chapterDoc = row.firestoreData;
            // Parts own the media now. The legacy single `media` object is still
            // checked so a chapter uploaded before the change does not read as
            // empty -- it reads as needing an import into a part.
            const partCount = chapterDoc?.partCount ?? 0;
            const totalMs = chapterDoc?.totalDurationMs ?? chapterDoc?.media?.durationMs ?? 0;
            const hasLegacyOnly = partCount === 0 && Boolean(chapterDoc?.media?.videoUrl);

            return (
              <div key={chId}>
                <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                      {row.chapter.number}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{row.chapter.titleMarathi}</p>
                      <p className="text-xs text-gray-500">{row.chapter.titleEnglish}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs font-medium text-gray-500">
                      {row.firestoreData?.totalOvis
                        ? `${row.firestoreData.totalOvis} ovis`
                        : '—'}
                    </span>
                    <span
                      className={`text-xs ${partCount > 0 ? 'text-purple-600' : hasLegacyOnly ? 'text-amber-600' : 'text-gray-300'}`}
                    >
                      {partCount > 0
                        ? `${partCount} part${partCount > 1 ? 's' : ''}`
                        : hasLegacyOnly
                          ? 'Needs import'
                          : 'No media'}
                    </span>
                    {(partCount > 0 || hasLegacyOnly) && (
                      <span
                        className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                          chapterDoc?.mediaReleased ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {chapterDoc?.mediaReleased ? 'Released' : 'On hold'}
                      </span>
                    )}
                    <span className={`text-xs ${totalMs ? 'text-blue-600' : 'text-gray-300'}`}>
                      {totalMs ? formatDuration(totalMs) : '—'}
                    </span>
                    <button
                      onClick={() => setExpanded(isExpanded ? null : chId)}
                      className="flex items-center gap-1 text-xs text-amber-600 font-medium hover:underline"
                    >
                      {isExpanded ? 'Hide' : 'Manage'}
                      {isExpanded ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    <ChapterManager
                      chapterId={chId}
                      chapter={row.firestoreData}
                      onChapterSaved={loadCounts}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>



    </div>
  );
}
